import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import axiosInstance from '../../../api/axiosInstance';
import { useOnboarding } from '../../../context/OnboardingContext';
import { LuHistory } from 'react-icons/lu';
import OnboardingModal from '../onboarding/OnboardingModal';
import OnboardingAlert from '../onboarding/OnboardingAlert';

const ResumeWizardModal = ({ isOpen, onClose, onResume, onSkip }) => {
  const navigate = useNavigate();
  const { restoreWizardData,resetOnboarding } = useOnboarding();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleResume = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Call the API to get the saved wizard data
      const response = await axiosInstance.get('/wizard/get_wizard_step');

      if (response.data && response.data.data) {
        resetOnboarding(); // Clear any existing state before restoring from API data
        const wizardData = response.data;

        // Calculate last in_progress step BEFORE restoring state (from API response data)
        const lastInProgressStep = wizardData.engine?.flow?.stepOrder?.filter(
          stepId => wizardData.engine?.steps?.[stepId]?.status === 'in_progress'
        ).pop() || 2;

        console.log('[ResumeWizardModal] Last in-progress step from API:', lastInProgressStep);

        // Restore the wizard state from the API response
        restoreWizardData(wizardData);

        // Close modal and navigate after a short delay to allow state update
        setTimeout(() => {
          onClose();
          if (onResume) {
            onResume();
          }

          // Navigate to the last in_progress step
          navigate(`/app/onboarding?step=${lastInProgressStep}`);
        }, 100);
      }
    } catch (err) {
      console.error('Error fetching wizard draft:', err);
      setError(err.response?.data?.message || 'Failed to resume setup. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onClose();
    if (onSkip) {
      onSkip();
    }
  };

  return (
    <OnboardingModal
      title="Resume your setup?"
      description="We found a previous setup session that wasn't completed."
      icon={<LuHistory size={20} />}
      tone="primary"
      dismissible={false}
      busy={isLoading}
      footer={
        <>
          <button type="button" className="ob-btn ob-btn--ghost" onClick={handleSkip} disabled={isLoading}>
            Skip for Now
          </button>
          <button
            type="button"
            className="ob-btn ob-btn--primary"
            onClick={handleResume}
            disabled={isLoading}
            data-autofocus
          >
            {isLoading && <span className="ob-spinner" aria-hidden="true" />}
            {isLoading ? 'Restoring...' : 'Resume Setup'}
          </button>
        </>
      }
    >
      {error && (
        <OnboardingAlert tone="danger" title="Couldn't restore your session">
          <p>{error}</p>
        </OnboardingAlert>
      )}
      <p>Pick up where you left off — your progress and preferences are saved and ready to restore.</p>
    </OnboardingModal>
  );
};

export default ResumeWizardModal;
